"use client"

import * as React from "react"
import { Check, Database, Search, Tag, Clock, User } from "lucide-react"
import { cn } from "../lib/utils"
import { Button } from "./button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"
import { Badge } from "./badge"

// Type definitions for saved prompts
export interface SavedPrompt {
  id: string
  title: string
  prompt: string
  description?: string
  category?: string
  tags?: string[]
  created_by?: string
  created_at?: string
  updated_at?: string
  usage_count?: number
  is_public?: boolean
}

interface PromptSelectorProps {
  prompts: SavedPrompt[]
  onSelectPrompt: (prompt: SavedPrompt) => void
  disabled?: boolean
  className?: string
  loading?: boolean
}

export function PromptSelector({
  prompts,
  onSelectPrompt,
  disabled = false,
  className,
  loading = false
}: PromptSelectorProps) {
  const [open, setOpen] = React.useState(false)
  const [selectedPrompt, setSelectedPrompt] = React.useState<SavedPrompt | null>(null)

  // Group prompts by category
  const groupedPrompts = React.useMemo(() => {
    const groups: Record<string, SavedPrompt[]> = {}
    
    prompts.forEach((prompt) => {
      const category = prompt.category || 'General'
      if (!groups[category]) {
        groups[category] = []
      }
      groups[category].push(prompt)
    })
    
    return groups
  }, [prompts])

  const handleSelectPrompt = (prompt: SavedPrompt) => {
    setSelectedPrompt(prompt)
    onSelectPrompt(prompt)
    setOpen(false)
  }

  const formatCategory = (category: string) => {
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "p-2 hover:bg-muted rounded-md transition-colors",
            className
          )}
          disabled={disabled || loading}
        >
          <Database className="h-5 w-5 text-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command>
          <div className="flex items-center border-b px-3">
            <CommandInput 
              placeholder="Search saved prompts..." 
              className="border-0 focus:ring-0"
            />
          </div>
          <CommandList className="max-h-72 overflow-y-auto">
              <CommandEmpty>
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <Database className="h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">No prompts found</p>
                </div>
              </CommandEmpty>
              
              {Object.entries(groupedPrompts).map(([category, categoryPrompts]) => (
                <div key={category}>
                  <CommandGroup heading={formatCategory(category)}>
                    {categoryPrompts.map((prompt) => (
                      <CommandItem
                        key={prompt.id}
                        value={`${prompt.title} ${prompt.description || ''} ${prompt.tags?.join(' ') || ''}`}
                        onSelect={() => handleSelectPrompt(prompt)}
                        className="flex flex-col items-start space-y-1 p-3 cursor-pointer"
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center space-x-2">
                            <Check
                              className={cn(
                                "h-4 w-4",
                                selectedPrompt?.id === prompt.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span className="font-medium text-sm truncate max-w-48">
                              {prompt.title}
                            </span>
                          </div>
                          {prompt.is_public && (
                            <Badge variant="secondary" className="text-xs">
                              Public
                            </Badge>
                          )}
                        </div>
                        
                        {prompt.description && (
                          <p className="text-xs text-gray-600 ml-6 line-clamp-2">
                            {prompt.description}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between w-full ml-6 mt-1">
                          <div className="flex items-center space-x-3 text-xs text-gray-500">
                            {prompt.usage_count !== undefined && (
                              <span className="flex items-center space-x-1">
                                <Clock className="h-3 w-3" />
                                <span>Used {prompt.usage_count} times</span>
                              </span>
                            )}
                            {prompt.created_at && (
                              <span>{formatDate(prompt.created_at)}</span>
                            )}
                          </div>
                        </div>
                        
                        {prompt.tags && prompt.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 ml-6 mt-1">
                            {prompt.tags.slice(0, 3).map((tag) => (
                              <Badge 
                                key={tag} 
                                variant="outline" 
                                className="text-xs px-1 py-0"
                              >
                                <Tag className="h-2 w-2 mr-1" />
                                {tag}
                              </Badge>
                            ))}
                            {prompt.tags.length > 3 && (
                              <Badge variant="outline" className="text-xs px-1 py-0">
                                +{prompt.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandSeparator />
                </div>
              ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// Hook for fetching saved prompts from Supabase
export function useSavedPrompts() {
  const [prompts, setPrompts] = React.useState<SavedPrompt[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const fetchPrompts = React.useCallback(async (search?: string, category?: string) => {
    setLoading(true)
    setError(null)
    
    try {
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      if (category && category !== 'all') params.append('category', category)
      
      const response = await fetch(`/api/admin/prompts?${params}`)
      
      if (!response.ok) {
        throw new Error('Failed to fetch prompts')
      }
      
      const data = await response.json()
      setPrompts(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch prompts')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    fetchPrompts()
  }, [fetchPrompts])

  return { prompts, loading, error, refetch: fetchPrompts }
}